/**
 * AccuFix Skin Analyzer – storefront script (v3)
 * v3: scan history, pregnancy-safe routines, mobile number, progress check on repeat scans
 * - Secure login via Shopify App Proxy token (falls back to customer id header)
 * - Client-side photo shrinking (EXIF-aware), selfie capture
 * - Consent, event tracking, scan id attached to cart for order attribution
 * - Every dynamic value is escaped before it touches the DOM
 */
(function () {
  "use strict";

  document.addEventListener("DOMContentLoaded", init);

  // ---------------------------------------------------------------- helpers
  var ESC_MAP = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (c) { return ESC_MAP[c]; });
  }
  // API text arrives HTML-escaped (for the old storefront). Decode, then escape once.
  var decoder = document.createElement("textarea");
  function txt(v) {
    decoder.innerHTML = String(v == null ? "" : v);
    return esc(decoder.value);
  }
  function safeUrl(u, allowRelative) {
    u = String(u || "");
    if (allowRelative && /^\/(?!\/)/.test(u)) return u;
    return /^https:\/\//i.test(u) ? u : "";
  }
  function attr(el, name, fallback) {
    var v = el.getAttribute("data-" + name);
    return v === null || v === "" ? fallback : v;
  }
  function uid() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function init() {
    var root = document.getElementById("skin-analyzer");
    if (!root) return;

    // --------------------------------------------------------------- config
    var cfg = {
      apiUrl: attr(root, "api-url", ""),
      proxyPath: attr(root, "proxy-path", "/apps/skinscan"),
      loggedIn: attr(root, "logged-in", "false") === "true",
      customerId: attr(root, "customer-id", ""),
      shopId: attr(root, "shop-id", ""),
      moneyFormat: attr(root, "money-format", "Rs.{{amount}}"),
      whatsapp: attr(root, "whatsapp-number", "").replace(/[^0-9]/g, ""),
      customerPhone: attr(root, "customer-phone", ""),
      // Built-in compressor: AI models read photos at 768 px on the short side – no need for more.
      aiShort: 768,
      aiLong: 1280,
      maxPhotos: Math.min(3, parseInt(attr(root, "max-photos", "3"), 10) || 3),
      consentVersion: attr(root, "consent-version", "v2-analysis-and-ai-improvement"),
      t: function (key, fallback) { return attr(root, key, fallback); },
    };
    var eventsUrl = cfg.apiUrl.replace(/\/chat\/?$/, "/events");
    var apiBase = cfg.apiUrl.replace(/\/chat\/?$/, ""); // e.g. https://…/api/v1

    var el = {
      uploadView: document.getElementById("skin-analyzer-upload-view"),
      resultsView: document.getElementById("skin-analyzer-results-view"),
      dropArea: document.getElementById("file-drop-area"),
      fileInput: document.getElementById("file-input"),
      cameraInput: document.getElementById("camera-input"),
      browseBtn: document.getElementById("browse-button"),
      cameraBtn: document.getElementById("camera-button"),
      analyzeBtn: document.getElementById("analyze-button"),
      loading: document.getElementById("loading-indicator"),
      error: document.getElementById("error-message"),
      photoList: document.getElementById("photo-list"),
      fileName: document.getElementById("file-name"),
      skinType: document.getElementById("skin-type"),
      consent: document.getElementById("consent-processing"),
      loginModal: document.getElementById("login-modal"),
      historyView: document.getElementById("skin-analyzer-history-view"),
      pregnancyNote: document.getElementById("pregnancy-note"),
    };

    var state = { photos: [], token: null, scanId: null, busy: false, me: null, phone: "" };

    var sessionId;
    try {
      sessionId = sessionStorage.getItem("skinscan_sid") || uid();
      sessionStorage.setItem("skinscan_sid", sessionId);
    } catch (e) {
      sessionId = uid();
    }

    // -------------------------------------------------------------- tracking
    function track(type, meta) {
      if (!eventsUrl) return;
      var payload = JSON.stringify({
        type: type,
        session_id: sessionId,
        token: state.token,
        logged_in: cfg.loggedIn,
        scan_id: state.scanId,
        page: location.pathname,
        meta: meta || {},
      });
      try {
        var blob = new Blob([payload], { type: "text/plain" });
        if (navigator.sendBeacon && navigator.sendBeacon(eventsUrl, blob)) return;
      } catch (e) { /* fall through */ }
      try {
        fetch(eventsUrl, { method: "POST", body: payload, headers: { "Content-Type": "text/plain" }, keepalive: true });
      } catch (e) { /* ignore */ }
    }

    // ------------------------------------------------------- secure identity
    function fetchToken() {
      if (!cfg.loggedIn || !cfg.proxyPath) return Promise.resolve(null);
      return fetch(cfg.proxyPath.replace(/\/$/, "") + "/token", { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (d && d.ok && d.token) { state.token = d.token; state.tokenExp = Number(d.exp) || 0; }
          return state.token;
        })
        .catch(function () { return null; });
    }
    var tokenReady = fetchToken();
    tokenReady.then(function () {
      track("page_view");
      if (state.token) loadMe();
      if (/scan-history/.test(location.hash)) openHistory();
    });

    function apiGet(path) {
      return fetch(apiBase + path, { headers: { "X-SCAN-TOKEN": state.token || "", Accept: "application/json" } }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) { if (!r.ok) throw new Error(j.error || "Request failed"); return j; });
      });
    }

    // ------------------------------------------------------- mobile number
    var PHONE_RE = /^(?:\+92|0)(\d{10})$/;
    function cleanPhone(v) { return String(v || "").replace(/[\s\-().]/g, ""); }
    function loadMe() {
      return apiGet("/me").then(function (me) {
        state.me = me;
        if (me.phone_required) openPhoneModal();
        showScansLeft(me);
        return me;
      }).catch(function () { return null; });
    }
    function showScansLeft(me) {
      if (!el.analyzeBtn || me.scans_left == null) return;
      var p = document.getElementById("scans-left");
      if (!p) {
        p = document.createElement("p");
        p.id = "scans-left";
        p.className = "scans-left";
        el.analyzeBtn.insertAdjacentElement("afterend", p);
      }
      p.textContent = me.scans_left > 0
        ? cfg.t("text-scans-left", "You have {n} of {m} free scans left this month.").replace("{n}", me.scans_left).replace("{m}", me.scans_per_month)
        : cfg.t("text-scans-none", "You've used all your scans for this month.") + (me.resets_on ? " " + cfg.t("text-scans-reset", "They reset on") + " " + new Date(me.resets_on).toLocaleDateString(undefined, { day: "numeric", month: "long" }) + "." : "");
      p.classList.toggle("scans-left--none", me.scans_left <= 0);
    }
    function openPhoneModal(message) {
      var old = document.getElementById("skin-phone-modal");
      if (old) old.remove();
      var pre = cleanPhone(cfg.customerPhone);
      if (!PHONE_RE.test(pre)) pre = /^92\d{10}$/.test(pre.replace(/^\+/, "")) ? "+" + pre.replace(/^\+/, "") : "";
      var wrap = document.createElement("div");
      wrap.id = "skin-phone-modal";
      wrap.className = "login-modal skin-phone-modal";
      wrap.setAttribute("role", "dialog");
      wrap.setAttribute("aria-modal", "true");
      wrap.setAttribute("aria-labelledby", "skin-phone-title");
      wrap.innerHTML =
        '<form class="login-message-container bordered-box" novalidate>' +
          '<h3 class="bordered-box-title" id="skin-phone-title">' + esc(cfg.t("text-phone-title", "Add your mobile number")) + "</h3>" +
          '<div class="login-message-content">' +
            "<p>" + esc(message || cfg.t("text-phone-why", "We need your mobile number once, so our skin expert can help you with your routine.")) + "</p>" +
            '<label class="skin-phone-label" for="skin-phone-input">' + esc(cfg.t("text-phone-label", "Mobile number")) + "</label>" +
            '<input id="skin-phone-input" class="skin-phone-input" type="tel" inputmode="tel" autocomplete="tel" placeholder="03001234567 or +923001234567" value="' + esc(pre) + '" required>' +
            '<p class="skin-phone-hint">' + esc(cfg.t("text-phone-hint", "Format: +92 or 0, then 10 digits")) + "</p>" +
            '<p class="skin-phone-error" role="alert"></p>' +
            '<button type="submit" class="analyze-button skin-phone-save">' + esc(cfg.t("text-phone-save", "Save & continue")) + "</button>" +
          "</div>" +
        "</form>";
      el.uploadView.parentNode.appendChild(wrap);
      document.body.style.overflow = "hidden";
      var input = wrap.querySelector("input");
      var err = wrap.querySelector(".skin-phone-error");
      var btn = wrap.querySelector("button");
      input.addEventListener("input", function () { err.textContent = ""; });
      setTimeout(function () { input.focus(); }, 50);
      wrap.querySelector("form").addEventListener("submit", function (e) {
        e.preventDefault();
        var v = cleanPhone(input.value);
        if (!PHONE_RE.test(v)) {
          err.textContent = cfg.t("text-phone-invalid", "Please enter +92 or 0 followed by 10 digits, e.g. 03001234567.");
          input.focus();
          return;
        }
        btn.disabled = true;
        fetch(apiBase + "/me", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-SCAN-TOKEN": state.token || "" },
          body: JSON.stringify({ phone: v }),
        })
          .then(function (r) { return r.json().then(function (j) { if (!r.ok || !j.ok) throw new Error(j.error || "Could not save"); return j; }); })
          .then(function () {
            state.phone = v;
            if (state.me) state.me.phone_required = false;
            wrap.remove();
            document.body.style.overflow = "";
            track("phone_saved");
          })
          .catch(function (ex) {
            // Saving separately failed (e.g. token expired) – send it with the scan instead.
            state.phone = v;
            if (state.me) state.me.phone_required = false;
            wrap.remove();
            document.body.style.overflow = "";
            if (ex && /log in/i.test(ex.message)) showError(ex.message);
          });
      });
    }

    // ------------------------------------------------------------ login gate
    function requireLogin(ev) {
      if (cfg.loggedIn) return false;
      if (ev) ev.preventDefault();
      if (el.loginModal) {
        el.loginModal.style.display = "flex";
        document.body.style.overflow = "hidden";
      }
      track("login_prompt_shown");
      return true;
    }
    if (el.loginModal) {
      el.loginModal.addEventListener("click", function (e) {
        if (e.target === el.loginModal || e.target.closest("[data-close-modal]")) {
          el.loginModal.style.display = "none";
          document.body.style.overflow = "";
        }
        if (e.target.closest("a.button")) track("login_clicked");
      });
    }

    // ------------------------------------------------------------- UI state
    function showError(msg) {
      if (!el.error) return;
      el.error.textContent = msg || "";
      el.error.style.display = msg ? "block" : "none";
    }
    function updateAnalyzeButton() {
      if (!el.analyzeBtn) return;
      var consentOk = !el.consent || el.consent.checked;
      el.analyzeBtn.disabled = !state.photos.length || !consentOk || state.busy;
    }
    function setLoading(on) {
      state.busy = on;
      if (el.loading) el.loading.style.display = on ? "block" : "none";
      updateAnalyzeButton();
    }
    function resetToUpload() {
      el.resultsView.style.display = "none";
      el.resultsView.innerHTML = "";
      if (el.historyView) el.historyView.style.display = "none";
      el.uploadView.style.display = "block";
      state.scanId = null;
      window.scrollTo({ top: el.uploadView.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
    }

    // ------------------------------------------------------ photo handling
    function loadImage(file) {
      if (window.createImageBitmap) {
        return createImageBitmap(file, { imageOrientation: "from-image" }).catch(function () {
          return createImageBitmap(file);
        });
      }
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    }

    // ---------------------------------------------------- built-in compressor
    // Resize to the size the AI reads (768 px short side, max 1280 long side), strip EXIF/GPS,
    // encode WebP (JPEG where the browser can't), and lower quality only down to a safe floor
    // so skin texture stays sharp for an accurate analysis.
    var TARGET_BYTES = 180 * 1024;
    var MIN_Q = 0.72;
    function encode(canvas, type, q) {
      return new Promise(function (res) { canvas.toBlob(res, type, q); });
    }
    function compressCanvas(canvas) {
      return encode(canvas, "image/webp", 0.82).then(function (b) {
        var type = b && b.type === "image/webp" ? "image/webp" : "image/jpeg";
        var step = function (q, best) {
          return encode(canvas, type, q).then(function (blob) {
            if (!blob) throw new Error("encode");
            if (blob.size <= TARGET_BYTES || q - 0.05 < MIN_Q) return blob;
            return step(Math.round((q - 0.05) * 100) / 100, blob);
          });
        };
        return step(type === "image/webp" ? 0.82 : 0.85);
      });
    }
    function drawScaled(src, sx, sy, sw, sh) {
      var scale = Math.min(1, cfg.aiShort / Math.min(sw, sh), cfg.aiLong / Math.max(sw, sh));
      var canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(sw * scale));
      canvas.height = Math.max(1, Math.round(sh * scale));
      var ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(src, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      return canvas;
    }
    function shrink(file) {
      return loadImage(file).then(function (img) {
        var w = img.width, h = img.height;
        if (w < 200 || h < 200) throw new Error("small");
        var canvas = drawScaled(img, 0, 0, w, h);
        if (img.close) img.close();
        return compressCanvas(canvas).then(function (b) { b.origSize = file.size; return b; });
      });
    }
    function kb(n) { return n >= 1048576 ? (n / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(n / 1024)) + " KB"; }

    function validFile(file) {
      if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "")) {
        track("photo_rejected", { reason: "type:" + file.type });
        return cfg.t("text-invalid-file", "Please choose a photo (JPG, PNG or WEBP).");
      }
      if (file.size > 25 * 1024 * 1024) {
        track("photo_rejected", { reason: "size" });
        return cfg.t("text-too-large", "This photo is too large. Please choose a smaller photo.");
      }
      return "";
    }

    function addPhotoBlob(blob, origSize) {
      if (state.photos.length >= cfg.maxPhotos) return false;
      state.photos.push({ blob: blob, url: URL.createObjectURL(blob), orig: origSize || blob.origSize || blob.size });
      return true;
    }

    function renderPhotos() {
      var n = state.photos.length;
      if (el.photoList) {
        el.photoList.innerHTML = state.photos
          .map(function (p, i) {
            return '<div class="photo-thumb"><img src="' + esc(p.url) + '" alt="" width="88" height="88">' +
              '<button type="button" class="photo-remove" data-remove="' + i + '" aria-label="' + esc(cfg.t("text-remove", "Remove photo")) + '">&times;</button></div>';
          })
          .join("");
        el.photoList.style.display = n ? "flex" : "none";
      }
      if (el.fileName) {
        var orig = state.photos.reduce(function (t, p) { return t + (p.orig || 0); }, 0);
        var now = state.photos.reduce(function (t, p) { return t + p.blob.size; }, 0);
        el.fileName.textContent = n
          ? n + " / " + cfg.maxPhotos + " " + cfg.t("text-photos-added", "photos added") +
            (orig > now ? " · " + cfg.t("text-compressed", "compressed") + " " + kb(orig) + " → " + kb(now) : "") +
            (n < cfg.maxPhotos ? " · " + cfg.t("text-add-more", "add more angles for a better result") : "")
          : "";
      }
      updateAnalyzeButton();
    }

    function addFiles(fileList) {
      showError("");
      var files = Array.prototype.slice.call(fileList || []);
      if (!files.length) return;
      var room = cfg.maxPhotos - state.photos.length;
      if (room <= 0) {
        showError(cfg.t("text-max-photos", "You can add up to 3 photos.").replace(/\d+/, cfg.maxPhotos));
        return;
      }
      if (files.length > room) {
        showError(cfg.t("text-max-photos", "You can add up to 3 photos.").replace(/\d+/, cfg.maxPhotos));
        files = files.slice(0, room);
      }
      var bad = "";
      files = files.filter(function (f) { var e = validFile(f); if (e) bad = e; return !e; });
      if (bad) showError(bad);
      if (!files.length) return;
      if (el.fileName) el.fileName.textContent = cfg.t("text-preparing", "Preparing photo…");
      Promise.all(
        files.map(function (f) {
          return shrink(f).then(
            function (b) { return { ok: true, blob: b }; },
            function (e) { return { ok: false, err: e }; }
          );
        })
      ).then(function (results) {
        var failed = 0;
        results.forEach(function (r) {
          if (r.ok) addPhotoBlob(r.blob, r.blob.origSize);
          else { failed++; track("photo_rejected", { reason: r.err && r.err.message }); }
        });
        if (failed) {
          showError(cfg.t("text-cannot-read", "We couldn't read one of the photos. Please use a clear JPG or PNG photo of your face."));
        }
        track("photo_selected", { count: state.photos.length });
        renderPhotos();
      });
    }

    if (el.photoList) {
      el.photoList.addEventListener("click", function (e) {
        var b = e.target.closest("[data-remove]");
        if (!b) return;
        e.preventDefault();
        e.stopPropagation();
        var i = parseInt(b.getAttribute("data-remove"), 10);
        var removed = state.photos.splice(i, 1)[0];
        if (removed) URL.revokeObjectURL(removed.url);
        showError("");
        renderPhotos();
      });
    }

    // ------------------------------------------------------- in-page camera
    var cam = null;
    function openCamera() {
      if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
        (el.cameraInput || el.fileInput).click();
        return;
      }
      if (state.photos.length >= cfg.maxPhotos) {
        showError(cfg.t("text-max-photos", "You can add up to 3 photos.").replace(/\d+/, cfg.maxPhotos));
        return;
      }
      var wrap = document.createElement("div");
      wrap.className = "skin-cam";
      wrap.setAttribute("role", "dialog");
      wrap.setAttribute("aria-modal", "true");
      wrap.innerHTML =
        '<div class="skin-cam__box">' +
          '<button type="button" class="skin-cam__close" data-cam="close" aria-label="Close">&times;</button>' +
          '<p class="skin-cam__hint">' + esc(cfg.t("text-cam-hint", "Fill the oval with your face, or hold the camera close to the skin you want checked (cheek, forehead, chin). No neck or shoulders. Good light.")) + "</p>" +
          '<div class="skin-cam__stage"><video playsinline muted autoplay></video><div class="skin-cam__oval" aria-hidden="true"></div></div>' +
          '<p class="skin-cam__count"></p>' +
          '<div class="skin-cam__actions">' +
            '<button type="button" class="browse-button skin-cam__shoot" data-cam="shoot">' + esc(cfg.t("text-cam-capture", "Take photo")) + "</button>" +
            '<button type="button" class="browse-button camera-button" data-cam="done">' + esc(cfg.t("text-cam-done", "Done")) + "</button>" +
          "</div>" +
          '<p class="skin-cam__error" role="alert"></p>' +
        "</div>";
      document.body.appendChild(wrap);
      document.body.style.overflow = "hidden";
      var video = wrap.querySelector("video");
      var count = wrap.querySelector(".skin-cam__count");
      var shoot = wrap.querySelector("[data-cam=shoot]");
      var errBox = wrap.querySelector(".skin-cam__error");
      cam = { wrap: wrap, stream: null };

      function updateCount() {
        count.textContent = state.photos.length + " / " + cfg.maxPhotos;
        shoot.disabled = state.photos.length >= cfg.maxPhotos;
      }
      updateCount();

      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false })
        .then(function (stream) {
          if (!cam) { stream.getTracks().forEach(function (t) { t.stop(); }); return; }
          cam.stream = stream;
          video.srcObject = stream;
        })
        .catch(function () {
          errBox.textContent = cfg.t("text-cam-denied", "We couldn't open your camera. Please allow camera access, or choose a photo instead.");
          shoot.disabled = true;
          track("photo_rejected", { reason: "camera_denied" });
        });

      wrap.addEventListener("click", function (e) {
        var b = e.target.closest("[data-cam]");
        if (!b && e.target !== wrap) return;
        var act = b ? b.getAttribute("data-cam") : "close";
        if (act === "shoot") {
          if (!video.videoWidth) return;
          // Crop to the face oval (plus a small margin) so the photo is a face close-up only.
          var vw = video.videoWidth, vh = video.videoHeight;
          var stage = video.getBoundingClientRect();
          var cover = Math.max(stage.width / vw, stage.height / vh);
          var visW = stage.width / cover, visH = stage.height / cover;
          var cropW = Math.min(vw, visW * 0.62 * 1.12), cropH = Math.min(vh, visH * 0.72 * 1.08);
          var sx = (vw - cropW) / 2, sy = (vh - cropH) / 2;
          var c = drawScaled(video, sx, sy, cropW, cropH);
          shoot.disabled = true;
          compressCanvas(c).then(function (blob) {
            if (blob && addPhotoBlob(blob)) {
              track("photo_selected", { count: state.photos.length, reason: "camera" });
              renderPhotos();
              wrap.classList.add("skin-cam--flash");
              setTimeout(function () { wrap.classList.remove("skin-cam--flash"); }, 150);
            }
            updateCount();
            if (state.photos.length >= cfg.maxPhotos) closeCamera();
          }).catch(function () { updateCount(); });
        } else {
          closeCamera();
        }
      });
    }
    function closeCamera() {
      if (!cam) return;
      if (cam.stream) cam.stream.getTracks().forEach(function (t) { t.stop(); });
      cam.wrap.remove();
      cam = null;
      document.body.style.overflow = "";
    }
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") closeCamera(); });

    // ------------------------------------------------------------- listeners
    if (el.dropArea) {
      el.dropArea.addEventListener("dragover", function (e) {
        e.preventDefault();
        if (!requireLogin(e)) el.dropArea.classList.add("dragover");
      });
      el.dropArea.addEventListener("dragleave", function () { el.dropArea.classList.remove("dragover"); });
      el.dropArea.addEventListener("drop", function (e) {
        e.preventDefault();
        el.dropArea.classList.remove("dragover");
        if (requireLogin(e)) return;
        if (e.dataTransfer && e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
      });
    }
    [el.fileInput, el.cameraInput].forEach(function (input) {
      if (!input) return;
      input.addEventListener("click", function (e) { requireLogin(e); });
      input.addEventListener("change", function (e) {
        if (e.target.files && e.target.files.length) addFiles(e.target.files);
        e.target.value = "";
      });
    });
    if (el.browseBtn) el.browseBtn.addEventListener("click", function (e) {
      e.preventDefault(); e.stopPropagation();
      if (!requireLogin(e)) el.fileInput.click();
    });
    if (el.cameraBtn) el.cameraBtn.addEventListener("click", function (e) {
      e.preventDefault(); e.stopPropagation();
      if (!requireLogin(e)) openCamera();
    });
    if (el.consent) el.consent.addEventListener("change", updateAnalyzeButton);
    function isPregnant() {
      var r = document.querySelector('input[name="skin-pregnant"]:checked');
      return !!(r && r.value === "true");
    }
    Array.prototype.forEach.call(document.querySelectorAll('input[name="skin-pregnant"]'), function (r) {
      r.addEventListener("change", function () { if (el.pregnancyNote) el.pregnancyNote.hidden = !isPregnant(); });
    });
    // "Click Here" → scan history
    document.addEventListener("click", function (e) {
      var a = e.target.closest("[data-open-history]");
      if (!a) return;
      e.preventDefault();
      if (requireLogin(e)) return;
      openHistory();
    });
    window.addEventListener("hashchange", function () { if (/scan-history/.test(location.hash)) openHistory(); });
    if (el.analyzeBtn) el.analyzeBtn.addEventListener("click", analyze);

    // --------------------------------------------------------------- analyze
    function analyze() {
      if (requireLogin()) return;
      if (!state.photos.length) return showError(cfg.t("text-select-photo", "Please add a photo first."));
      if (el.consent && !el.consent.checked) return showError(cfg.t("text-consent-required", "Please tick the consent box to continue."));
      if (state.me && state.me.phone_required && !state.phone) return openPhoneModal();

      setLoading(true);
      showError("");
      track("analyze_clicked");

      // Tokens last 2 hours – get a fresh one if the page has been open a long time.
      if (state.token && state.tokenExp && Date.now() > state.tokenExp - 5 * 60 * 1000) {
        state.token = null;
        tokenReady = fetchToken();
      }
      tokenReady.then(function () {
        var fd = new FormData();
        state.photos.forEach(function (p, i) { fd.append("file", p.blob, "scan_" + (i + 1) + (p.blob.type === "image/webp" ? ".webp" : ".jpg")); });
        fd.append("orig_bytes", state.photos.map(function (p) { return p.orig || p.blob.size; }).join(","));
        var skin = el.skinType && el.skinType.value;
        if (skin) {
          fd.append("skin_type", skin);
          fd.append("user_message", "My skin type is " + skin);
        }
        fd.append("consent_processing", "true");
        // The required consent text covers analysis AND AI improvement, so both are recorded.
        fd.append("consent_training", "true");
        fd.append("consent_version", cfg.consentVersion);
        fd.append("session_id", sessionId);
        fd.append("pregnant", isPregnant() ? "true" : "false");
        fd.append("client_v", "3");
        if (state.phone) fd.append("phone", state.phone);

        var headers = { "X-SHOP-ID": cfg.shopId };
        if (state.token) headers["X-SCAN-TOKEN"] = state.token;
        else if (cfg.customerId) headers["X-CUSTOMER-ID"] = cfg.customerId;

        return fetch(cfg.apiUrl, { method: "POST", body: fd, headers: headers }).then(function (res) {
          return res.text().then(function (body) {
            if (res.status === 428) { var pe = new Error(body); pe.phone = true; throw pe; }
            if (!res.ok) throw new Error(body && body.length < 300 ? body : cfg.t("error-subtext", "Something went wrong."));
            var data;
            try { data = JSON.parse(body); } catch (e) { throw new Error(cfg.t("error-subtext", "Something went wrong.")); }
            return data;
          });
        });
      })
        .then(function (data) {
          state.scanId = data.scan_id || null;
          el.uploadView.style.display = "none";
          el.resultsView.innerHTML = renderResults(data, state.photos.map(function (p) { return p.url; }), false);
          el.resultsView.style.display = "block";
          bindResults();
          window.scrollTo({ top: el.resultsView.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
          track("result_shown", { count: countProducts(data) });
          if (state.me && state.me.scans_left != null) { state.me.scans_left = Math.max(0, state.me.scans_left - 1); showScansLeft(state.me); }
        })
        .catch(function (err) {
          if (err && err.phone) { openPhoneModal(err.message); return; }
          el.uploadView.style.display = "none";
          el.resultsView.innerHTML = renderError(err && err.message);
          el.resultsView.style.display = "block";
          bindResults();
          track("result_error", { reason: err && err.message });
        })
        .then(function () { setLoading(false); });
    }

    // -------------------------------------------------------------- render
    var STEPS = [
      ["cleanse", "step-cleanse-title", "Cleanse"],
      ["treat", "step-treat-title", "Treat"],
      ["moisturize", "step-moisturize-title", "Moisturize"],
      ["spf", "step-protect-title", "Protect"],
    ];

    function countProducts(d) {
      var s = (d && d.skincare_steps) || {};
      return STEPS.reduce(function (n, k) { return n + ((s[k[0]] || []).length); }, 0);
    }

    function formatMoney(price) {
      var n = parseFloat(String(price || "").replace(/[^0-9.]/g, ""));
      if (!isFinite(n)) return cfg.t("text-price-na", "Price not available");
      var withSep = function (num, dec, sep, decSep) {
        var parts = num.toFixed(dec).split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
        return parts.join(decSep);
      };
      var out = cfg.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, k) {
        switch (k) {
          case "amount_no_decimals": return withSep(n, 0, ",", ".");
          case "amount_with_comma_separator": return withSep(n, 2, ".", ",");
          case "amount_no_decimals_with_comma_separator": return withSep(n, 0, ".", ",");
          case "amount_with_apostrophe_separator": return withSep(n, 2, "'", ".");
          default: return withSep(n, 2, ",", ".");
        }
      });
      decoder.innerHTML = out.replace(/<[^>]*>/g, "");
      return esc(decoder.value);
    }

    function usageHtml(u) {
      var morning = esc(cfg.t("usage-time-morning-label", "Morning Use"));
      var evening = esc(cfg.t("usage-time-evening-label", "Evening Use"));
      var both = esc(cfg.t("usage-time-both-label", "Morning/Evening Use"));
      var sun = '<svg class="product-usage-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>';
      var moon = '<svg class="product-usage-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>';
      u = String(u || "both").toLowerCase();
      if (u === "morning") return '<span title="' + morning + '">' + sun + " " + morning + "</span>";
      if (u === "evening") return '<span title="' + evening + '">' + moon + " " + evening + "</span>";
      return '<span title="' + both + '">' + sun + '<span class="usage-plus-sign">+</span>' + moon + " " + both + "</span>";
    }

    function productCard(p) {
      var rating = Number(p.rating);
      var stars = rating >= 1 && rating <= 5
        ? '<div class="product-rating" aria-label="' + rating + ' out of 5">' +
          "★★★★★".slice(0, Math.round(rating)) + "☆☆☆☆☆".slice(0, 5 - Math.round(rating)) + "</div>"
        : "";
      var url = safeUrl(p.product_url, true);
      var img = safeUrl(p.image_url, false);
      var variant = /^\d+$/.test(String(p.variant_id || "")) ? String(p.variant_id) : "";
      return (
        '<div class="product-card">' +
          '<div class="product-image-container">' + (img ? '<img src="' + esc(img) + '" alt="' + txt(p.product_name) + '" loading="lazy">' : "") + "</div>" +
          '<div class="product-info">' +
            "<h4>" + txt(p.product_name) + "</h4>" +
            '<div class="product-info-row">' + stars + '<span class="product-usage">' + usageHtml(p.usage_time) + "</span></div>" +
            '<div class="product-price">' + formatMoney(p.price) + "</div>" +
            '<p class="product-why"><strong>' + esc(cfg.t("why-recommended-title", "Why We Recommend This:")) + "</strong> " + txt(p.why_it_works) + "</p>" +
            (url ? '<a href="' + esc(url) + '" target="_blank" rel="noopener" class="product-view-link" data-track="product_view_clicked" data-product-id="' + esc(p.product_id) + '">' + esc(cfg.t("view-product-text", "Learn more")) + "</a>" : "") +
            (variant
              ? '<div class="product-buttons"><button type="button" class="btn btn-secondary add-to-cart-button" data-variant-id="' + variant + '" data-product-id="' + esc(p.product_id) + '">' + esc(cfg.t("add-to-cart-text", "Add to Cart")) + "</button></div>"
              : "") +
          "</div>" +
        "</div>"
      );
    }

    function fmtDate(iso) {
      var d = new Date(iso);
      if (isNaN(d)) return "";
      try {
        return d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
      } catch (e) { return d.toISOString().slice(0, 16).replace("T", " "); }
    }

    function renderResults(d, photoUrls, fromHistory) {
      var s = d.skincare_steps || {};
      var concerns = (d.skin_concerns || []).filter(Boolean);
      photoUrls = photoUrls || [];
      var html =
        '<div class="skin-analysis-page">' +
          '<div class="results-header"><h1>' + esc(cfg.t("results-page-title", "Your Skin Analysis")) + "</h1>" +
            (fromHistory
              ? '<button type="button" class="back-button no-print" data-action="history">← ' + esc(cfg.t("text-back-history", "Back to history")) + "</button>"
              : '<button type="button" class="close-button no-print" data-action="restart" title="' + esc(cfg.t("close-button-title", "Start New Analysis")) + '">&times;</button>') +
          "</div>" +
          (d.created_at ? '<p class="results-date">' + esc(cfg.t("text-scanned-on", "Scanned on")) + " " + esc(fmtDate(d.created_at)) + "</p>" : "") +
          (d.pregnancy_safe ? '<div class="pregnancy-banner">🤰 ' + esc(cfg.t("text-pregnancy-banner", "Pregnancy & breastfeeding-safe routine. Please check new skincare with your doctor.")) + "</div>" : "") +
          (d.progress ? renderProgress(d.progress) : "") +
          '<div class="skin-analysis-columns">' +
            '<div class="column-left">' +
              '<div class="analysis-box bordered-box"><div class="analysis-box-header bordered-box-title"><h3>' + esc(cfg.t("uploaded-image-title", "Your Uploaded Image")) + "</h3></div>" +
                '<div class="analysis-box-content user-image-container' + (photoUrls.length > 1 ? " user-image-container--multi" : "") + '">' +
                  photoUrls.map(function (u) { return '<img class="user-image" src="' + esc(u) + '" alt="">'; }).join("") +
                "</div></div>" +
              '<div class="analysis-box bordered-box"><div class="analysis-box-header bordered-box-title"><h3>' + esc(cfg.t("skin-concerns-title", "Identified Skin Concerns")) + "</h3></div>" +
                '<div class="analysis-box-content"><ul class="skin-concerns-list">' +
                  (concerns.length
                    ? concerns.map(function (c) { return '<li><span class="check-icon">✓</span> ' + txt(c) + "</li>"; }).join("")
                    : "<li><em>" + esc(cfg.t("no-concerns-text", "No specific skin concerns identified.")) + "</em></li>") +
                "</ul></div></div>" +
            "</div>" +
            '<div class="column-right">' +
              '<div class="analysis-box bordered-box"><div class="analysis-box-header bordered-box-title"><h3>' + esc(cfg.t("analysis-title", "Analysis of Your Skin")) + "</h3></div>" +
                '<div class="analysis-box-content"><div class="analysis-description">' + (d.condition_description ? txt(d.condition_description) : esc(cfg.t("no-analysis-text", "No detailed analysis available for this image."))) + "</div>" +
                (d.general_advice ? '<div class="general-advice"><h4>' + esc(cfg.t("general-advice-title", "General Advice:")) + "</h4><p>" + txt(d.general_advice) + "</p></div>" : "") +
              "</div></div>" +
              renderRoutine(s) +
            "</div>" +
          "</div>" +
          renderHelp() +
          (fromHistory ? "" : '<div class="history-cta no-print"><a href="#scan-history" class="history-link" data-open-history>' + esc(cfg.t("text-view-history", "View my scan history")) + " →</a></div>") +
        "</div>";
      return html;
    }

    function list(items) {
      return items && items.length ? "<ul>" + items.map(function (x) { return "<li>" + txt(x) + "</li>"; }).join("") + "</ul>" : "";
    }
    function renderProgress(p) {
      var f = p.facts || {};
      var labels = {
        improved: ["good", cfg.t("text-progress-improved", "Your skin has improved")],
        same: ["same", cfg.t("text-progress-same", "No big change yet")],
        worse: ["worse", cfg.t("text-progress-worse", "Your skin needs extra care")],
        unclear: ["same", cfg.t("text-progress-unclear", "Progress check")],
      };
      var lab = labels[p.verdict] || labels.unclear;
      var stats = [];
      stats.push("<li><b>" + esc(String(f.days_since)) + "</b> " + esc(cfg.t("text-days-since", "days since your last scan")) + " (" + esc(fmtDate(p.previous_date)) + ")</li>");
      if (f.days_on_routine != null) stats.push("<li><b>~" + esc(String(f.days_on_routine)) + "</b> " + esc(cfg.t("text-days-routine", "days using your routine")) + "</li>");
      if (f.recommended_count) stats.push("<li><b>" + esc(String(f.bought_count)) + " / " + esc(String(f.recommended_count)) + "</b> " + esc(cfg.t("text-products-bought", "recommended products ordered")) + "</li>");
      var html =
        '<div class="analysis-box bordered-box progress-box progress-box--' + lab[0] + '"><div class="analysis-box-header bordered-box-title"><h3>' + esc(lab[1]) + "</h3></div>" +
          '<div class="analysis-box-content">' +
            (p.summary ? "<p>" + txt(p.summary) + "</p>" : "") +
            '<ul class="progress-stats">' + stats.join("") + "</ul>" +
            (f.resolved && f.resolved.length ? "<h4>" + esc(cfg.t("text-cleared", "Cleared since last time")) + "</h4>" + list(f.resolved) : "") +
            (p.improvements && p.improvements.length ? "<h4>" + esc(cfg.t("text-better", "What got better")) + "</h4>" + list(p.improvements) : "") +
            (p.factors && p.factors.length ? "<h4>" + esc(cfg.t("text-factors", "What is affecting your results")) + "</h4>" + list(p.factors) : "") +
            (f.skin_cycle_note ? '<p class="progress-note">' + txt(f.skin_cycle_note) + "</p>" : "") +
            (p.next_steps ? "<h4>" + esc(cfg.t("text-next-steps", "Your next steps")) + "</h4><p>" + txt(p.next_steps) + "</p>" : "") +
            (p.care && p.care.show ? renderCare(p.care) : "") +
          "</div></div>";
      return html;
    }
    function renderCare(c) {
      var wa = cfg.whatsapp
        ? '<a href="https://wa.me/' + cfg.whatsapp + '?text=' + encodeURIComponent("Hi, I'd like a free review of my skin routine (scan " + (state.scanId || "") + ")") + '" target="_blank" rel="noopener" class="whatsapp-button" data-track="whatsapp_clicked">WhatsApp</a>'
        : "";
      return '<div class="care-box">' +
        (c.text ? "<p>" + txt(c.text) + "</p>" : "") +
        (c.code ? '<p class="care-code">' + esc(cfg.t("text-care-code", "Your thank-you code:")) + " <b>" + txt(c.code) + "</b></p>" : "") +
        wa + "</div>";
    }

    // --------------------------------------------------------- scan history
    var historyCache = null;
    function showOnly(view) {
      el.uploadView.style.display = view === "upload" ? "block" : "none";
      el.resultsView.style.display = view === "results" ? "block" : "none";
      if (el.historyView) el.historyView.style.display = view === "history" ? "block" : "none";
    }
    function openHistory() {
      if (!el.historyView) return;
      if (requireLogin()) return;
      showOnly("history");
      el.historyView.innerHTML = '<div class="skin-analysis-page"><div class="results-header"><h1>' + esc(cfg.t("text-history-title", "Your Scan History")) + '</h1></div><div class="loading-indicator" style="display:block"><div class="spinner"></div></div></div>';
      window.scrollTo({ top: el.historyView.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
      tokenReady
        .then(function () { if (!state.token) throw new Error(cfg.t("text-history-login", "Please log in to see your scan history.")); return apiGet("/history"); })
        .then(function (j) { historyCache = j.scans || []; renderHistory(); track("history_viewed", { count: historyCache.length }); })
        .catch(function (e) {
          el.historyView.innerHTML = '<div class="skin-analysis-page"><div class="results-header"><h1>' + esc(cfg.t("text-history-title", "Your Scan History")) + '</h1><button type="button" class="close-button no-print" data-action="restart">&times;</button></div><p class="error-message" style="display:block">' + esc(e.message) + "</p></div>";
        });
    }
    var VERDICT = { improved: "↑ Improved", same: "→ No change", worse: "↓ Needs care", unclear: "Progress check" };
    function renderHistory() {
      var items = historyCache || [];
      var body = items.length
        ? items.map(function (h, i) {
            var steps = (h.result && h.result.skincare_steps) || {};
            var prods = [];
            ["cleanse", "treat", "moisturize", "spf"].forEach(function (k) { (steps[k] || []).forEach(function (p) { prods.push(txt(p.product_name)); }); });
            return '<div class="history-card bordered-box">' +
              '<div class="history-photos">' + (h.photos || []).slice(0, 3).map(function (u) { return '<img src="' + esc(safeUrl(u, false)) + '" alt="" loading="lazy" width="72" height="72">'; }).join("") + "</div>" +
              '<div class="history-info">' +
                '<p class="history-date">' + esc(fmtDate(h.created_at)) + "</p>" +
                '<p class="history-type"><b>' + txt(h.skin_type) + "</b>" + (h.progress_verdict ? ' <span class="history-badge history-badge--' + esc(h.progress_verdict) + '">' + esc(VERDICT[h.progress_verdict] || h.progress_verdict) + "</span>" : "") + (h.pregnant ? ' <span class="history-badge">🤰</span>' : "") + "</p>" +
                (h.concerns && h.concerns.length ? '<p class="history-concerns">' + h.concerns.slice(0, 5).map(txt).join(" · ") + "</p>" : "") +
                (prods.length ? '<p class="history-products">' + esc(cfg.t("text-recommended", "Recommended:")) + " " + prods.join(", ") + "</p>" : "") +
                '<button type="button" class="browse-button history-open" data-action="history-open" data-index="' + i + '">' + esc(cfg.t("text-view-result", "View full result")) + "</button>" +
              "</div></div>";
          }).join("")
        : '<div class="bordered-box history-empty"><p>' + esc(cfg.t("text-history-empty", "You haven't done a skin scan yet.")) + "</p></div>";
      el.historyView.innerHTML =
        '<div class="skin-analysis-page"><div class="results-header"><h1>' + esc(cfg.t("text-history-title", "Your Scan History")) + "</h1>" +
          '<button type="button" class="close-button no-print" data-action="restart" title="' + esc(cfg.t("close-button-title", "Start New Analysis")) + '">&times;</button></div>' +
          '<div class="history-list">' + body + "</div>" +
          '<div class="history-cta"><button type="button" class="analyze-button" data-action="restart">' + esc(cfg.t("text-new-scan", "Start a new scan")) + "</button></div>" +
        "</div>";
      el.historyView.onclick = historyClick;
    }
    function historyClick(e) {
      var t = e.target.closest("[data-action]");
      if (!t) return;
      var a = t.getAttribute("data-action");
      if (a === "restart") { e.preventDefault(); if (location.hash) history.replaceState(null, "", location.pathname + location.search); return resetToUpload(); }
      if (a === "history-open") {
        var h = (historyCache || [])[Number(t.getAttribute("data-index"))];
        if (!h || !h.result) return;
        state.scanId = h.scan_id;
        el.resultsView.innerHTML = renderResults(h.result, h.photos || [], true);
        showOnly("results");
        bindResults();
        window.scrollTo({ top: el.resultsView.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
      }
    }

    function renderRoutine(s) {
      var n = 1, body = "", variants = [];
      STEPS.forEach(function (st) {
        var items = (s[st[0]] || []).filter(Boolean);
        if (!items.length) return;
        items.forEach(function (p) { if (/^\d+$/.test(String(p.variant_id || ""))) variants.push(String(p.variant_id)); });
        body += '<div class="step-container"><h3 class="step-title">' + esc(cfg.t("text-step", "Step")) + " " + n++ + ": " + esc(cfg.t(st[1], st[2])) + "</h3>" +
          items.map(productCard).join("") + '</div><div class="step-divider"></div>';
      });
      if (!body && !s.usage_instructions) return "";
      if (s.usage_instructions) {
        body += '<div class="usage-instructions"><h3><strong>' + esc(cfg.t("usage-instructions-title", "How to Use These Products")) + "</strong></h3><p>" + txt(s.usage_instructions) + "</p></div>";
      }
      if (variants.length) {
        body += '<div class="step-divider"></div><div class="add-all-products-container"><button type="button" class="add-all-products-button" data-action="add-routine" data-variants="' + variants.join(",") + '">' +
          esc(cfg.t("add-routine-text", "ADD ROUTINE TO CART")) + "</button></div>";
      }
      return '<div class="analysis-box routine-box bordered-box"><div class="analysis-box-header bordered-box-title"><h3>' +
        esc(cfg.t("skincare-routine-title", "Your Personalized Skincare Routine")) + '</h3></div><div class="analysis-box-content">' + body + "</div></div>";
    }

    function renderHelp() {
      var wa = cfg.whatsapp
        ? '<div class="whatsapp-button-container"><a href="https://wa.me/' + cfg.whatsapp + '" target="_blank" rel="noopener" class="whatsapp-button" data-track="whatsapp_clicked">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981z"/></svg> WhatsApp</a></div>'
        : "";
      var notes = [cfg.t("info-1", ""), cfg.t("info-2", ""), cfg.t("info-3", "")].filter(Boolean);
      return (
        '<div class="questions-help-box bordered-box"><div class="analysis-box-header bordered-box-title"><h3>' + esc(cfg.t("help-title", "Got Questions About your Skincare Routine?")) + "</h3></div>" +
          '<div class="analysis-box-content questions-help-content"><p>' + esc(cfg.t("help-text", "We're here to help! Tap the WhatsApp button below for quick answers.")) + "</p>" + wa + "</div></div>" +
        (notes.length
          ? '<div class="important-info-box"><h3>' + esc(cfg.t("info-title", "Important Information:")) + "</h3><ul>" + notes.map(function (x) { return "<li>" + esc(x) + "</li>"; }).join("") + "</ul></div>"
          : "")
      );
    }

    function renderError(message) {
      return (
        '<div class="skin-analysis-page"><div class="results-header"><h1>' + esc(cfg.t("error-page-title", "Analysis Error")) + "</h1>" +
          '<button type="button" class="close-button no-print" data-action="restart" title="' + esc(cfg.t("try-again-button-text", "Try Again")) + '">&times;</button></div>' +
        '<div class="analysis-box bordered-box error-container"><div class="analysis-box-header bordered-box-title"><h3>' + esc(cfg.t("error-heading", "We encountered a problem")) + "</h3></div>" +
          '<div class="analysis-box-content"><div class="error-icon">❗</div><p class="error-message">' + esc(message || "") + "</p><p>" +
          esc(cfg.t("error-subtext", "This could be due to a connectivity issue or a problem with our service.")) + "</p>" +
          '<div class="error-actions"><button type="button" class="btn btn-primary try-again-button" data-action="restart">' + esc(cfg.t("try-again-button-text", "Try Again")) + "</button></div></div></div></div>"
      );
    }

    // ------------------------------------------------------------- cart
    function addToCart(variantIds) {
      var items = variantIds.map(function (id) {
        return { id: Number(id), quantity: 1, properties: state.scanId ? { _skin_scan_id: state.scanId } : {} };
      });
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items }),
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error((j && (j.description || j.message)) || "Could not add to cart"); });
        if (!state.scanId) return r;
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ attributes: { skin_scan_id: state.scanId } }),
        }).catch(function () {});
      });
    }

    function bindResults() {
      el.resultsView.onclick = function (e) {
        var t = e.target.closest("[data-action],[data-track],.add-to-cart-button");
        if (!t) return;
        var action = t.getAttribute("data-action");
        if (action === "restart") { e.preventDefault(); return resetToUpload(); }
        if (action === "history") { e.preventDefault(); showOnly("history"); return; }

        if (t.classList.contains("add-to-cart-button")) {
          e.preventDefault();
          if (t.disabled) return;
          t.disabled = true;
          var label = t.textContent;
          addToCart([t.getAttribute("data-variant-id")])
            .then(function () {
              t.textContent = cfg.t("text-added", "Added ✓");
              track("add_to_cart", { product_id: t.getAttribute("data-product-id"), variant_id: t.getAttribute("data-variant-id") });
              document.dispatchEvent(new CustomEvent("cart:refresh"));
            })
            .catch(function (err) { t.disabled = false; t.textContent = label; alertInline(t, err.message); });
          return;
        }

        if (action === "add-routine") {
          e.preventDefault();
          if (t.disabled) return;
          t.disabled = true;
          var ids = (t.getAttribute("data-variants") || "").split(",").filter(Boolean);
          addToCart(ids)
            .then(function () {
              track("add_routine_to_cart", { count: ids.length });
              window.location.href = "/cart";
            })
            .catch(function (err) { t.disabled = false; alertInline(t, err.message); });
          return;
        }

        var tr = t.getAttribute("data-track");
        if (tr) track(tr, { product_id: t.getAttribute("data-product-id") || undefined });
      };
    }

    function alertInline(anchor, msg) {
      var p = document.createElement("p");
      p.className = "error-message";
      p.style.display = "block";
      p.textContent = msg;
      anchor.parentNode.appendChild(p);
      setTimeout(function () { p.remove(); }, 5000);
    }
  }
})();
